/* React */
import { MouseEvent, useEffect, useMemo, useRef, useState } from 'react'

/* Redux */
import AppBarRedux from './AppBar.Redux'
import NavigationRedux from 'src/layout/Navigation/Navigation.Redux'

/* Next */
import Link from 'next/link'
import { useRouter } from 'next/router'

/* Material */
import MuiAppBar from '@mui/material/AppBar'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import IconButton from '@mui/material/IconButton'
import Badge from '@mui/material/Badge'
import Toolbar from '@mui/material/Toolbar'
import Typo from '@mui/material/Typography'
import useStyles from './styles'

/* Material-Icons */
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import FavoriteIcon from '@mui/icons-material/Favorite'
import LocalMallIcon from '@mui/icons-material/LocalMall'
import MenuIcon from '@mui/icons-material/Menu'
import SearchIcon from '@mui/icons-material/Search'
import TuneIcon from '@mui/icons-material/Tune'

/* Components */
import Suche from './Suche'
import ContactModal from 'src/components/ContactModal/ContactModal'
import MagazineModal from 'src/components/Magazine/MagazineModal'
import Logo from 'template/logos/logo.svg'
import shopInfos from 'template/infos'
import config from 'src/config'
import { PageChrome, resolvePageBuilder } from 'src/pageBuilder/config'
import { MenuPageBuilder, MenuPageBuilderBannerBlock, MenuPageBuilderContactSubject } from 'src/shared/dbTypes/menuPageBuilder'
import useNavigationPaths from 'src/layout/navigationPaths'
import { hrefForNavigationItem } from 'src/functions/navigationBreadcrumbs'
import type { LandingPageImageAiGenerated } from 'src/functions/landingPageImageAiGenerated'

/* Types */
import { item, tMenu } from 'src/shared/frontendTypes/navigation'

const FILTER_PATHNAMES = [
  '/suche',
  '/m/[m_alias]/[mid]',
  '/b/[b_alias]/[bid]',
  '/b/[b_alias]/[bid]/m/[m_alias]/[mid]',
] as const

type BuilderNavigationDisplay = 'none' | 'separate' | 'inline'

const MAX_INLINE_NAV_ITEMS = 6

const toCssLength = (value?: number | string) =>
  typeof value === 'number' ? `${value}px` : value

const resolveImageSrc = (image: string | { src?: string }) =>
  typeof image === 'string' ? image : image.src || ''

const findTopLevelActiveItem = (
  paths: tMenu,
  activeMenuId: number | null,
  activeBrandId: number | null
): number | null => {
  const matches = (entry: item): boolean => {
    if (activeMenuId && entry.menueId === activeMenuId) return true
    if (activeBrandId && entry.brand === activeBrandId) return true
    return !!entry.children?.some(matches)
  }

  const active = paths.find(matches)
  return active?.id ?? null
}

const getHref = (entry: item) => {
  return hrefForNavigationItem(entry) || '#'
}

const normalizeNavigationPath = (path: string) =>
  path.split('#')[0].split('?')[0]

const magazineHash = '#magazin'

const isMagazineHashActive = () =>
  typeof window !== 'undefined' && window.location.hash.toLowerCase().startsWith(magazineHash)

const getCurrentUrlWithoutHash = () =>
  typeof window === 'undefined'
    ? ''
    : `${window.location.pathname}${window.location.search}`

const useMagazineModalState = () => {
  const [magazineOpen, setMagazineOpen] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return

    const syncFromHash = () => setMagazineOpen(isMagazineHashActive())
    syncFromHash()
    window.addEventListener('hashchange', syncFromHash)
    window.addEventListener('popstate', syncFromHash)

    return () => {
      window.removeEventListener('hashchange', syncFromHash)
      window.removeEventListener('popstate', syncFromHash)
    }
  }, [])

  const closeMagazine = () => {
    setMagazineOpen(false)
    if (typeof window === 'undefined' || !isMagazineHashActive()) return
    window.history.replaceState(null, '', getCurrentUrlWithoutHash())
  }

  return {
    magazineOpen,
    closeMagazine,
  }
}

const useBuilderNavigation = () => {
  const router = useRouter()
  const paths = useNavigationPaths()
  const {
    setDisplayMenuId,
    setDisplayFilter,
    resetFilter,
  } = NavigationRedux()
  const { toggleDrawer } = AppBarRedux()

  const activeMenuId = router.query.mid ? Number(router.query.mid) : null
  const activeBrandId = router.query.bid ? Number(router.query.bid) : null
  const activeTopLevelId = useMemo(
    () => findTopLevelActiveItem(paths, activeMenuId, activeBrandId),
    [paths, activeMenuId, activeBrandId]
  )

  const topEntries = useMemo(() => paths.filter(entry => entry.title !== 'Home'), [paths])
  const isFilterRoute = FILTER_PATHNAMES.includes(router.pathname as (typeof FILTER_PATHNAMES)[number])

  const openMenuDrawer = () => {
    setDisplayFilter(false)
    setDisplayMenuId(0)
    toggleDrawer()
  }

  const openFilterDrawer = () => {
    setDisplayFilter(true)
    toggleDrawer()
  }

  const handleEntryClick = (entry: item, event?: MouseEvent<HTMLElement>) => {
    const href = getHref(entry)
    const hasTarget = href !== '#'

    if (entry.children?.length && !hasTarget) {
      event?.preventDefault()
      setDisplayFilter(false)
      setDisplayMenuId(entry.id)
      toggleDrawer()
      return
    }

    if (hasTarget && (entry.menueId || entry.brand)) {
      setDisplayFilter(false)
      if (normalizeNavigationPath(router.asPath) !== normalizeNavigationPath(href)) {
        resetFilter()
      }
    }
  }

  return {
    topEntries,
    activeTopLevelId,
    isFilterRoute,
    openMenuDrawer,
    openFilterDrawer,
    handleEntryClick,
  }
}

const LegacyToolbarContent: React.FC = () => {
  const classes = useStyles()

  const {
    openMenuDrawer,
    openSearchOverlay,
    toggleDrawer,
    warenkorbItems, wunschlisteItems,
    userToken,
  } = AppBarRedux()

  const { displayFilter } = NavigationRedux()

  return <>
    <IconButton
      edge='start'
      className={classes.menuButton}
      color='inherit'
      aria-label='menu'
      onClick={openMenuDrawer}
      size='large'
    >
      <MenuIcon />
    </IconButton>

    <div className={classes.title}>
      <Link href='/' passHref legacyBehavior>
        <Typo component='a' variant='h6'>
          Home
        </Typo>
      </Link>
    </div>

    {displayFilter &&
      <Button
        title='Filter anzeigen'
        className={classes.buttonFilter}
        color='inherit'
        variant='outlined'
        onClick={toggleDrawer}
      >
        Filter
      </Button>
    }

    <div className={classes.spacer} />

    <IconButton
      title='Im Shop suchen'
      color='inherit'
      onClick={openSearchOverlay}
      size='large'
    >
      <SearchIcon />
    </IconButton>

    <Link href='/kundenkonto' passHref legacyBehavior>
      <IconButton
        aria-label='Mein Kundenkonto'
        color='inherit'
        size='large'
        className={classes.buttonKonto}
      >
        <Badge
          badgeContent={userToken ? 1 : 0}
          color='secondary'
        >
          <AccountCircleIcon />
        </Badge>
      </IconButton>
    </Link>

    <Link href='/wunschliste' passHref legacyBehavior>
      <IconButton
        aria-label='Meine Wunschliste'
        color='inherit'
        size='large'
        className={classes.buttonFavs}
      >
        <Badge badgeContent={wunschlisteItems} color='secondary'>
          <FavoriteIcon />
        </Badge>
      </IconButton>
    </Link>

    <Link href='/warenkorb' passHref legacyBehavior>
      <IconButton
        aria-label='Mein Warenkorb'
        color='inherit'
        size='large'
        className={classes.cartButton}
      >
        <Badge badgeContent={warenkorbItems} color='secondary'>
          <LocalMallIcon />
        </Badge>
      </IconButton>
    </Link>

  </>
}

interface BuilderToolbarContentProps {
  navigationDisplay: BuilderNavigationDisplay
}

const BuilderToolbarContent: React.FC<BuilderToolbarContentProps> = ({
  navigationDisplay,
}) => {
  const classes = useStyles()
  const isInlineNavigation = navigationDisplay === 'inline'

  const {
    openSearchOverlay,
    warenkorbItems,
    wunschlisteItems,
    userToken,
  } = AppBarRedux()

  const {
    topEntries,
    activeTopLevelId,
    isFilterRoute,
    openMenuDrawer,
    openFilterDrawer,
    handleEntryClick,
  } = useBuilderNavigation()
  const showInlineNavigation = isInlineNavigation && topEntries.length > 0
  const logoSrc = resolveImageSrc(Logo)

  return <>
    <IconButton
      edge='start'
      className={classes.builderMenuButton}
      color='inherit'
      aria-label='Menü öffnen'
      onClick={openMenuDrawer}
      size='large'
    >
      <MenuIcon />
    </IconButton>

    <div className={[classes.toolbarBrand, isInlineNavigation ? classes.toolbarBrandInline : ''].filter(Boolean).join(' ')}>
      <Link href='/' passHref legacyBehavior>
        <a className={classes.brandLink} aria-label={shopInfos.name}>
          <img
            src={logoSrc}
            alt={shopInfos.name}
          />
        </a>
      </Link>
    </div>

    {showInlineNavigation &&
      <nav className={classes.builderInlineNav} aria-label='Hauptmenü'>
        <div className={classes.builderInlineNavScroller}>
          {topEntries.slice(0, MAX_INLINE_NAV_ITEMS).map(entry => {
            const href = getHref(entry)
            const isActive = activeTopLevelId === entry.id

            return <Link key={entry.id} href={href} passHref legacyBehavior>
              <Button
                component='a'
                className={[
                  classes.builderNavButton,
                  classes.builderInlineNavButton,
                  isActive ? 'active' : '',
                ].join(' ')}
                onClick={event => handleEntryClick(entry, event)}
              >
                {entry.title}
              </Button>
            </Link>
          })}
        </div>
      </nav>
    }

    {!showInlineNavigation && <div className={classes.spacer} />}

    <div className={classes.builderActions}>
      {isFilterRoute &&
        <IconButton
          aria-label='Filter öffnen'
          color='inherit'
          size='large'
          onClick={openFilterDrawer}
          className={classes.builderFilterButton}
        >
          <TuneIcon />
        </IconButton>
      }

      <IconButton
        aria-label='Suche öffnen'
        color='inherit'
        size='large'
        onClick={openSearchOverlay}
      >
        <SearchIcon />
      </IconButton>

      <Link href='/wunschliste' passHref legacyBehavior>
        <IconButton
          aria-label='Meine Wunschliste'
          color='inherit'
          size='large'
          className={classes.builderDesktopOnlyAction}
        >
          <Badge badgeContent={wunschlisteItems} color='secondary'>
            <FavoriteIcon />
          </Badge>
        </IconButton>
      </Link>

      <Link href='/kundenkonto' passHref legacyBehavior>
        <IconButton
          aria-label='Mein Kundenkonto'
          color='inherit'
          size='large'
          className={classes.builderDesktopOnlyAction}
        >
          <Badge
            badgeContent={userToken ? 1 : 0}
            color='secondary'
          >
            <AccountCircleIcon />
          </Badge>
        </IconButton>
      </Link>

      <Link href='/warenkorb' passHref legacyBehavior>
        <IconButton
          aria-label='Mein Warenkorb'
          color='inherit'
          size='large'
          className={classes.cartButton}
        >
          <Badge badgeContent={warenkorbItems} color='secondary'>
            <LocalMallIcon />
          </Badge>
        </IconButton>
      </Link>
    </div>

  </>
}

const BuilderDesktopNavigation: React.FC = () => {
  const classes = useStyles()
  const {
    topEntries,
    activeTopLevelId,
    handleEntryClick,
  } = useBuilderNavigation()

  if (topEntries.length === 0) return null

  return <div className={classes.builderDesktopNav}>
    <div className={classes.builderDesktopNavScroller}>
      {topEntries.map(entry => {
        const href = getHref(entry)
        const isActive = activeTopLevelId === entry.id

        return <Link key={entry.id} href={href} passHref legacyBehavior>
          <Button
            component='a'
            className={[classes.builderNavButton, isActive ? 'active' : ''].join(' ')}
            onClick={event => handleEntryClick(entry, event)}
          >
            {entry.title}
          </Button>
        </Link>
      })}
    </div>
  </div>
}

const BuilderMobileNavigation: React.FC = () => {
  return null
}

const getTopStripParts = (value?: string) =>
  String(value || '')
    .split(/\r?\n/)
    .map(part => part.trim())
    .filter(Boolean)

const toAlphaColor = (value: string | undefined, opacityPercent: number, fallback: string) => {
  const hex = String(value || '').trim().replace('#', '')
  const opacity = Math.max(0, Math.min(100, opacityPercent)) / 100

  if (!/^[0-9a-f]{6}$/i.test(hex)) return fallback

  const red = Number.parseInt(hex.slice(0, 2), 16)
  const green = Number.parseInt(hex.slice(2, 4), 16)
  const blue = Number.parseInt(hex.slice(4, 6), 16)

  return `rgba(${red}, ${green}, ${blue}, ${opacity})`
}

const isLightColor = (value?: string) => {
  const color = String(value || '').trim().toLowerCase()
  if (color === 'white') return true

  let hex = color.replace('#', '')
  if (/^[0-9a-f]{3}$/i.test(hex)) {
    hex = hex.split('').map(part => `${part}${part}`).join('')
  }
  if (!/^[0-9a-f]{6}$/i.test(hex)) return false

  const red = Number.parseInt(hex.slice(0, 2), 16)
  const green = Number.parseInt(hex.slice(2, 4), 16)
  const blue = Number.parseInt(hex.slice(4, 6), 16)
  const luminance = (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255

  return luminance > 0.72
}

const renderTopStripGroup = (
  parts: string[],
  href: string | undefined,
  textClassName: string,
  anchorClassName: string,
  keyPrefix: string,
  onContactModal?: () => void
) => {
  if (parts.length === 0) return null

  const content = parts.map((part, index) =>
    <span key={`${keyPrefix}-${index}`} className={textClassName}>
      {part}
    </span>
  )

  if (!href) return content

  if ((href === '#contact-modal' || href === '#kontaktmodal') && onContactModal) {
    return <button type='button' className={anchorClassName} onClick={onContactModal}>
      {content}
    </button>
  }

  return <a href={href} className={anchorClassName}>
    {content}
  </a>
}

interface Props {
  headerHeight: number
  pageChrome: PageChrome
  pageBuilder?: MenuPageBuilder
  landingPageImageAiGenerated?: LandingPageImageAiGenerated
}

const HeaderAppBar: React.FC<Props> = ({
  headerHeight,
  pageChrome,
  pageBuilder,
  landingPageImageAiGenerated,
}) => {
  const classes = useStyles()
  const appBarRef = useRef<HTMLDivElement>(null)
  const router = useRouter()
  const { showMobileSuche } = AppBarRedux()
  const resolvedBuilder = resolvePageBuilder(pageBuilder)
  const topStripBanner = resolvedBuilder.blocks.find((block): block is MenuPageBuilderBannerBlock =>
    block.type === 'banner' && (block.variant || 'topStrip') === 'topStrip'
  )

  const [legacyFixed, setLegacyFixed] = useState(false)
  const [builderScrolled, setBuilderScrolled] = useState(false)
  const [builderHeight, setBuilderHeight] = useState(0)
  const [contactModalOpen, setContactModalOpen] = useState(false)
  const [contactModalSubject, setContactModalSubject] = useState<MenuPageBuilderContactSubject>('sonstiges')
  const { magazineOpen, closeMagazine } = useMagazineModalState()
  const topStripTitleParts = getTopStripParts(topStripBanner?.title)
  const topStripSubtitleParts = getTopStripParts(topStripBanner?.subtitle)
  const topStripBodyParts = getTopStripParts(topStripBanner?.body)
  const hasTopStripBanner = Boolean(topStripBanner)
  const navigationDisplay = resolvedBuilder.layout.navigationDisplay

  const openContactModal = (subject: MenuPageBuilderContactSubject = 'sonstiges') => {
    setContactModalSubject(subject)
    setContactModalOpen(true)
  }

  const isBuilderTop = pageChrome.builderEnabled && pageChrome.navigationPosition === 'top'
  const intrinsicBuilderHeader = isBuilderTop && pageChrome.hideLegacyHeader
  const overlayHeroHeader =
    isBuilderTop
    && router.pathname === '/'
    && Boolean(resolvedBuilder.hero?.imageSrc)
  const appBarPosition = isBuilderTop
    ? 'fixed'
    : legacyFixed
      ? 'fixed'
      : 'static'
  const isOverlayHeroChrome = overlayHeroHeader && !builderScrolled
  const overlayNavigationBackgroundColor = isLightColor(resolvedBuilder.theme.navigationBackgroundColor)
    ? '#15100d'
    : resolvedBuilder.theme.navigationBackgroundColor
  const currentNavigationBackgroundColor = isOverlayHeroChrome
    ? overlayNavigationBackgroundColor
    : resolvedBuilder.theme.navigationBackgroundColor
  const useLightLogo = isBuilderTop
    && !isLightColor(currentNavigationBackgroundColor)
  const headerLogoConfig = config.headerLogo
  const headerLogoCssVars = headerLogoConfig
    ? {
        ['--builder-logo-max-width' as string]: toCssLength(headerLogoConfig.builderMaxWidth),
        ['--builder-logo-max-height' as string]: toCssLength(headerLogoConfig.builderMaxHeight),
        ['--builder-logo-inline-max-width' as string]: toCssLength(headerLogoConfig.builderInlineMaxWidth),
        ['--builder-logo-mobile-max-width' as string]: toCssLength(headerLogoConfig.builderMobileMaxWidth),
        ['--builder-logo-mobile-max-height' as string]: toCssLength(headerLogoConfig.builderMobileMaxHeight),
      }
    : {}
  const builderAppBarStyle = isBuilderTop
    ? {
        ...headerLogoCssVars,
        backgroundColor: isOverlayHeroChrome
          ? toAlphaColor(
              overlayNavigationBackgroundColor,
              resolvedBuilder.theme.navigationOverlayOpacity,
              'rgba(43, 32, 23, 0.72)'
            )
          : builderScrolled
            ? 'var(--page-builder-nav-bg, #ffffff)'
            : 'var(--page-builder-nav-bg, #ffffff)',
        color: isOverlayHeroChrome
          ? '#ffffff'
          : 'var(--page-builder-nav-text, #1f2933)',
        fontFamily: 'var(--page-builder-font-family, inherit)',
        borderBottom: isOverlayHeroChrome
          ? 'none'
          : '1px solid rgba(15, 23, 42, 0.08)',
        boxShadow: isOverlayHeroChrome
          ? 'none'
          : builderScrolled
            ? '0 18px 48px rgba(15, 23, 42, 0.12)'
            : '0 10px 34px rgba(15, 23, 42, 0.08)',
        ['--builder-logo-filter' as string]: useLightLogo
          ? 'brightness(0) invert(1)'
          : 'none',
        backdropFilter: 'blur(22px) saturate(180%)',
        WebkitBackdropFilter: 'blur(22px) saturate(180%)',
      }
    : undefined
  const collapseTopStrip = isBuilderTop && hasTopStripBanner && builderScrolled
  const builderSpacerHeight = builderHeight || (hasTopStripBanner ? 126 : 86)
  const topStripStyle = topStripBanner
    ? {
        opacity: collapseTopStrip ? 0 : 1,
        ...(collapseTopStrip
          ? { ['--builder-top-strip-max-height' as string]: '0px' }
          : {}),
        backgroundColor: topStripBanner.backgroundColor || '#222728',
        color: 'var(--page-builder-accent-contrast, #ffffff)',
      }
    : undefined

  useEffect(() => {
    if (!isBuilderTop) {
      setBuilderHeight(0)
      return
    }

    // Modern browsers use the intrinsic sticky header. Only the fixed-position
    // compatibility path needs a measured placeholder.
    if (intrinsicBuilderHeader && window.CSS?.supports?.('overflow', 'clip')) return
    if (!appBarRef.current || typeof ResizeObserver === 'undefined') return

    const element = appBarRef.current
    const updateHeight = () => setBuilderHeight(element.getBoundingClientRect().height)
    const observer = new ResizeObserver(updateHeight)

    observer.observe(element)
    updateHeight()

    return () => observer.disconnect()
  }, [isBuilderTop, intrinsicBuilderHeader, hasTopStripBanner])

  useEffect(() => {
    if (isBuilderTop) return

    const handleScroll = () => {
      setLegacyFixed(window.pageYOffset > headerHeight)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => window.removeEventListener('scroll', handleScroll)
  }, [headerHeight, isBuilderTop])

  useEffect(() => {
    if (!isBuilderTop) return

    const handleScroll = () => {
      const currentScrollTop = window.pageYOffset
      setBuilderScrolled(currentScrollTop > 12)
    }

    handleScroll()
    const raf = window.requestAnimationFrame(handleScroll)
    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.cancelAnimationFrame(raf)
      window.removeEventListener('scroll', handleScroll)
    }
  }, [isBuilderTop, router.asPath])

  return (
    <div className={[classes.appBar, intrinsicBuilderHeader && overlayHeroHeader ? classes.builderOverlay : ''].join(' ')}>
      {isBuilderTop && !overlayHeroHeader && <div className={intrinsicBuilderHeader ? classes.builderSpacer : undefined} aria-hidden style={{ height: builderSpacerHeight }} />}
      {!isBuilderTop && legacyFixed && <Toolbar />}

      <MuiAppBar
        ref={appBarRef}
        position={appBarPosition}
        color='primary'
        enableColorOnDark
        className={isBuilderTop ? [classes.builderAppBar, intrinsicBuilderHeader ? classes.builderAppBarInFlow : ''].join(' ') : undefined}
        style={builderAppBarStyle}
      >
        {isBuilderTop && topStripBanner && (
          <div
            className={classes.builderTopStrip}
            style={topStripStyle}
          >
            <Container maxWidth={false}>
              <div className={classes.builderTopStripInner}>
                <div className={classes.builderTopStripDesktop}>
                  <div className={[classes.builderTopStripTexts, classes.builderTopStripLeft].join(' ')}>
                    {renderTopStripGroup(
                      topStripTitleParts,
                      topStripBanner.titleHref,
                      classes.builderTopStripTitle,
                      classes.builderTopStripAnchor,
                      'top-strip-title',
                      () => openContactModal('rueckruf')
                    )}
                  </div>

                  <div className={[classes.builderTopStripTexts, classes.builderTopStripCenter].join(' ')}>
                    {renderTopStripGroup(
                      topStripSubtitleParts,
                      topStripBanner.subtitleHref,
                      classes.builderTopStripMeta,
                      classes.builderTopStripAnchor,
                      'top-strip-subtitle',
                      () => openContactModal('sonstiges')
                    )}
                  </div>

                  <div className={[classes.builderTopStripTexts, classes.builderTopStripRight].join(' ')}>
                    {renderTopStripGroup(
                      topStripBodyParts,
                      topStripBanner.bodyHref,
                      classes.builderTopStripMeta,
                      classes.builderTopStripAnchor,
                      'top-strip-body',
                      () => openContactModal('termin')
                    )}
                  </div>
                </div>

                <div className={classes.builderTopStripMobile}>
                  <div className={classes.builderTopStripCompactWrap}>
                    {renderTopStripGroup(
                      topStripTitleParts,
                      topStripBanner.titleHref,
                      classes.builderTopStripCompact,
                      classes.builderTopStripAnchor,
                      'top-strip-mobile-title',
                      () => openContactModal('rueckruf')
                    )}
                    {renderTopStripGroup(
                      topStripSubtitleParts,
                      topStripBanner.subtitleHref,
                      classes.builderTopStripCompact,
                      classes.builderTopStripAnchor,
                      'top-strip-mobile-subtitle',
                      () => openContactModal('sonstiges')
                    )}
                    {renderTopStripGroup(
                      topStripBodyParts,
                      topStripBanner.bodyHref,
                      classes.builderTopStripCompact,
                      classes.builderTopStripAnchor,
                      'top-strip-mobile-body',
                      () => openContactModal('termin')
                    )}
                  </div>
                </div>
              </div>
            </Container>
          </div>
        )}

        <Container
          maxWidth={isBuilderTop ? false : 'lg'}
          className={isBuilderTop ? classes.builderContainer : undefined}
        >
          <Toolbar className={isBuilderTop ? classes.toolbarBuilder : classes.toolbar}>
            {isBuilderTop
              ? <BuilderToolbarContent navigationDisplay={navigationDisplay} />
              : <LegacyToolbarContent />
            }
          </Toolbar>

          {isBuilderTop && navigationDisplay === 'separate' && (
            <>
              <BuilderDesktopNavigation />
              <BuilderMobileNavigation />
            </>
          )}
        </Container>
      </MuiAppBar>

      <Suche
        open={showMobileSuche}
        pageBuilder={pageBuilder}
        landingPageImageAiGenerated={landingPageImageAiGenerated}
      />
      <ContactModal
        open={contactModalOpen}
        initialSubject={contactModalSubject}
        onClose={() => setContactModalOpen(false)}
      />
      <MagazineModal
        open={magazineOpen}
        onClose={closeMagazine}
        landingPageImageAiGenerated={landingPageImageAiGenerated}
      />
    </div>
  )
}

export default HeaderAppBar
